import React, { useState } from 'react';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';

const TestPage = () => {
  const [latexInput, setLatexInput] = useState('');
  const [renderError, setRenderError] = useState('');

  const handleInputChange = (e) => {
    setLatexInput(e.target.value);
    setRenderError('');
  };

  const sampleFormulas = [
    'x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}',
    'E = mc^2',
    '\\int_0^\\infty e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}',
    'PV = \\frac{FV_N}{(1 + r)^N}',
    'F(x) = \\int_{-\\infty}^x f(t) dt',
    '\\sum_{n=1}^\\infty \\frac{1}{n^2} = \\frac{\\pi^2}{6}'
  ];

  const insertSample = (formula) => {
    setLatexInput(formula);
    setRenderError('');
  };

  return (
    <div className="p-6 max-w-6xl mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 mb-8 rounded-lg shadow-lg">
        <h1 className="text-3xl font-bold text-center">
          LaTeX Math Expression Converter
        </h1>
        <p className="text-center mt-2 text-blue-100">
          Type mathematical expressions in LaTeX format and see them rendered below
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Input Section */}
        <div className="space-y-4">
          <div className="bg-gray-50 p-6 rounded-lg border-2 border-gray-200">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              LaTeX Input
            </h2>
            
            <textarea
              value={latexInput}
              onChange={handleInputChange}
              placeholder="Enter your LaTeX mathematical expression here...&#10;&#10;Examples:&#10;x = \\frac{-b \\pm \\sqrt{b^2 - 4ac}}{2a}&#10;E = mc^2&#10;\\int_0^\\infty e^{-x^2} dx = \\frac{\\sqrt{\\pi}}{2}"
              className="w-full h-64 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
              style={{ fontFamily: 'Monaco, Consolas, "Courier New", monospace' }}
            />
            
            <div className="mt-4 flex flex-wrap gap-2">
              <span className="text-sm font-medium text-gray-600">Quick samples:</span>
              {sampleFormulas.map((formula, index) => (
                <button
                  key={index}
                  onClick={() => insertSample(formula)}
                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs hover:bg-blue-200 transition-colors"
                >
                  Sample {index + 1}
                </button>
              ))}
            </div>
            
            <button
              onClick={() => setLatexInput('')}
              className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
            >
              Clear Input
            </button>
          </div>

          {/* Demo Notice */}
                    {/* LaTeX Syntax Help */}
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="font-semibold mb-2 text-yellow-800">LaTeX Syntax Help:</h3>
            <div className="text-sm text-yellow-700 space-y-1">
              <p><code>\\frac{"{a}"}{"{b}"}</code> → Fraction</p>
              <p><code>\\sqrt{"{x}"}</code> → Square root</p>
              <p><code>x^{"{n}"}</code> → Superscript</p>
              <p><code>x_{"{n}"}</code> → Subscript</p>
              <p><code>\\int_{"{a}"}^{"{b}"}</code> → Integral</p>
              <p><code>\\sum_{"{n=1}"}^{"{\\infty}"}</code> → Summation</p>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg">
            <h3 className="font-semibold mb-2 text-yellow-800">How to Use:</h3>
            <div className="text-sm text-yellow-700 space-y-2">
              <div>
                <p className="font-medium">📷 Image Upload:</p>
                <ul className="ml-4 list-disc space-y-1">
                  <li>Upload images containing mathematical expressions</li>
                  <li>Supported formats: JPG, PNG, GIF, WebP</li>
                  <li>Clear, high-contrast images work best</li>
                  <li>Handwritten or printed math both supported</li>
                </ul>
              </div>
              <div>
                <p className="font-medium">✏️ LaTeX Syntax:</p>
                <ul className="ml-4 list-disc space-y-1">
                  <li><code>$\\frac{"{a}"}{"{b}"}$</code> → Fraction</li>
                  <li><code>$\\sqrt{"{x}"}$</code> → Square root</li>
                  <li><code>$x^{"{n}"}$</code> → Superscript</li>
                  <li><code>$x_{"{n}"}$</code> → Subscript</li>
                </ul>
              </div>
              <p className="font-medium text-yellow-800 mt-2">💡 Tip: Upload clear images for better recognition accuracy</p>
            </div>
          </div>
        </div>

        {/* Output Section */}
        <div className="space-y-4">
          <div className="bg-white p-6 rounded-lg border-2 border-green-200 shadow-sm">
            <h2 className="text-2xl font-semibold mb-4 text-gray-800">
              Rendered Output
            </h2>
            
            <div className="min-h-64 p-4 bg-gray-50 rounded-lg border border-gray-200">
              {latexInput.trim() ? (
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded border">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Block Math:</h4>
                    <div className="text-center">
                      {(() => {
                        try {
                          return <BlockMath math={latexInput} />;
                        } catch (error) {
                          setRenderError(error.message);
                          return (
                            <div className="text-red-500 text-sm">
                              <p>Render Error:</p>
                              <p>{error.message}</p>
                            </div>
                          );
                        }
                      })()}
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded border">
                    <h4 className="text-sm font-medium text-gray-600 mb-2">Inline Math:</h4>
                    <p className="text-lg">
                      Here is an inline formula: {(() => {
                        try {
                          return <InlineMath math={latexInput} />;
                        } catch (error) {
                          return <span className="text-red-500 text-sm">Error: {error.message}</span>;
                        }
                      })()} within text.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-400">
                  <div className="text-center">
                    <div className="text-6xl mb-4">�</div>
                    <p className="text-lg font-medium mb-2">Start typing LaTeX to get started</p>
                    <p>Type LaTeX manually to see the rendered output</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Raw LaTeX Display */}
          {latexInput.trim() && (
            <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
              <h3 className="font-semibold mb-2 text-gray-700">Raw LaTeX Code:</h3>
              <code className="text-sm bg-white p-2 rounded border block overflow-x-auto">
                {latexInput}
              </code>
            </div>
          )}
        </div>
      </div>

      {/* Original Content - Collapsed by default */}
      <div className="mt-12">
        <details className="bg-gray-50 rounded-lg border">
          <summary className="p-4 font-semibold cursor-pointer hover:bg-gray-100 rounded-lg">
            📚 View Original Example Content (Present Value Formulas)
          </summary>
          <div className="p-6 border-t">
            {/* Previous content here - keeping the original example */}
            <div className="bg-red-600 text-white p-4 mb-6 rounded-t-lg">
              <div className="flex items-center space-x-4">
                <div className="bg-white text-red-600 px-3 py-1 rounded font-bold">
                  LOS
                </div>
                <div className="text-lg font-medium">
                  P1 & 2. calculate and interpret the future value (FV) and present value (PV) using timeline in modeling
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="border-l-4 border-red-600 pl-4">
                <h1 className="text-4xl font-bold text-gray-800 mb-4">
                  2a. Present Value: Single Cash Flow
                </h1>
              </div>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-lg mb-4">
                  We know that FV is found as: <InlineMath math="FV_N = PV(1 + r)^N" />. Hence, the PV is:
                </p>
                <div className="text-center my-6">
                  <BlockMath math="PV = \\frac{FV_N}{(1 + r)^N} = FV_N \\times (1 + r)^{-N}" />
                </div>
              </div>
              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-gray-700">
                  <strong>On calculator:</strong> If you found <InlineMath math="(1 + r)^N" /> then you may use{' '}
                  <span className="bg-black text-white px-2 py-1 rounded text-sm font-mono">1/x</span> button.
                </p>
              </div>
              <div className="border border-gray-200 p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-gray-800">Example Problem:</h3>
                <p className="text-lg mb-4">
                  <strong>I.e.,</strong> An insurance company has issued a GI Contract that promises to pay 
                  $100,000 in 6 years with an 8% return rate. What amount of money must 
                  the insurer invest today?
                </p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <div className="text-center mb-4">
                    <BlockMath math="PV = \\frac{100,000}{(1 + 0.08)^6} = 63,016.96" />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <strong>On calculator:</strong> N=6, I/Y=8, PMT=0, FV=+100 CPT PV=-63.01696.
                    </div>
                    <div className="text-red-600 text-sm font-medium">
                      <div className="border border-red-300 rounded p-2">
                        Negative sign implies<br />
                        you invest / pay!
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

export default TestPage;